import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar.js';
import { Eye, EyeOff, Menu } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UnifiedAssistantDrawer from './assistant/UnifiedAssistantDrawer.js';
import { Toaster } from './ui/toaster.js';
import { useRecentPagesTracker } from '../hooks/useRecentPages.js';
import { useTheme } from '../contexts/ThemeContext.js';
import ErrorBoundary from './ErrorBoundary.js';

const detectMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  try {
    const width = window.innerWidth || document.documentElement?.clientWidth || 0;
    return window.matchMedia?.('(max-width: 1023px)')?.matches === true || width < 1024;
  } catch (_error) {
    return true;
  }
};

const Layout = ({ pageTitle }) => {
  useRecentPagesTracker();
  const { themeName } = useTheme();
  const isLightTheme = themeName === 'light' || themeName === 'dashPro' || themeName === 'quietGlass';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => detectMobileViewport());
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('ui.sidebarCollapsed') === 'true'; } catch (_e) { return false; }
  });
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(() => {
    try { return localStorage.getItem('ui.sidebarHidden') === 'true'; } catch (_e) { return false; }
  });
  const { t } = useTranslation();
  const location = useLocation();
  const isEditorWorkspace = useMemo(() => new URLSearchParams(location.search).get('editor-preview') === '1', [location.search]);

  useEffect(() => {
    try { localStorage.setItem('ui.sidebarCollapsed', String(desktopSidebarCollapsed)); } catch (_e) {}
  }, [desktopSidebarCollapsed]);
  useEffect(() => {
    try { localStorage.setItem('ui.sidebarHidden', String(desktopSidebarHidden)); } catch (_e) {}
  }, [desktopSidebarHidden]);
  useEffect(() => {
    const handleResize = () => setIsMobileViewport(detectMobileViewport());
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  useEffect(() => {
    // Every route change closes the mobile drawer. Do not depend on Tailwind state classes.
    if (isMobileViewport) setSidebarOpen(false);
  }, [location.pathname, location.search, isMobileViewport]);

  const contentOffset = useMemo(() => {
    if (desktopSidebarHidden) return '0px';
    return desktopSidebarCollapsed ? '120px' : '272px';
  }, [desktopSidebarCollapsed, desktopSidebarHidden]);

  const toggleSidebarVisibility = () => {
    if (isMobileViewport) {
      setSidebarOpen((prev) => !prev);
      return;
    }
    setDesktopSidebarHidden((prev) => !prev);
  };

  return (_jsxs("div", {
    className: "layout-main runtime-safe-layout",
    style: { backgroundColor: 'var(--bg-primary)', minHeight: '100dvh', position: 'relative' },
    children: [
      isEditorWorkspace ? null : _jsx(Sidebar, {
        isOpen: sidebarOpen,
        onClose: () => setSidebarOpen(false),
        isCollapsed: isMobileViewport ? false : desktopSidebarCollapsed,
        isHidden: isMobileViewport ? false : desktopSidebarHidden,
      }),
      isEditorWorkspace ? null : _jsx("div", {
        className: "fixed bottom-4 left-4 z-[70] lg:bottom-6 lg:left-6",
        "data-testid": "floating-sidebar-visibility-wrapper",
        children: _jsx("button", {
          type: "button",
          onClick: toggleSidebarVisibility,
          className: `pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-2xl border backdrop-blur-2xl transition-all ${isLightTheme ? 'border-slate-200 bg-white/95 text-slate-700 shadow-lg' : 'border-white/10 bg-slate-950/85 text-slate-100 shadow-2xl'}`,
          title: isMobileViewport ? 'القائمة' : desktopSidebarHidden ? 'إظهار القائمة' : 'إخفاء القائمة',
          "aria-label": isMobileViewport ? 'Toggle Menu' : desktopSidebarHidden ? 'Show Sidebar' : 'Hide Sidebar',
          children: isMobileViewport ? _jsx(Menu, { size: 18 }) : desktopSidebarHidden ? _jsx(Eye, { size: 18 }) : _jsx(EyeOff, { size: 18 }),
        }),
      }),
      _jsxs("main", {
        className: "content-area runtime-safe-content",
        style: {
          backgroundColor: 'transparent',
          position: 'relative',
          zIndex: 10,
          '--content-offset': isEditorWorkspace ? '0px' : contentOffset,
        },
        children: [
          isEditorWorkspace ? null : _jsx("div", {
            className: "mobile-page-bar",
            children: _jsxs("div", {
              className: "mobile-page-bar-inner",
              children: [
                _jsx("button", {
                  type: "button",
                  onClick: () => setSidebarOpen(true),
                  className: "mobile-page-menu-button",
                  "aria-label": "Open Menu",
                  "data-testid": "mobile-sidebar-open-button",
                  children: _jsx(Menu, { size: 20 }),
                }),
                _jsx("h1", {
                  className: "mobile-page-title",
                  children: pageTitle || t('app.dashboard'),
                }),
                _jsx("span", { className: "mobile-page-spacer", "aria-hidden": "true" }),
              ],
            }),
          }),
          _jsx("div", {
            className: "page-runtime-shell",
            "data-testid": "page-runtime-shell",
            children: _jsx(ErrorBoundary, { children: _jsx(Outlet, {}) }),
          }),
        ],
      }),
      isEditorWorkspace ? null : _jsx(UnifiedAssistantDrawer, {}),
      _jsx(Toaster, {}),
    ],
  }));
};

export default Layout;
